import {
	absoluteUrl,
	canonicalSiteUrl,
	DEFAULT_OG_IMAGE_HEIGHT,
	DEFAULT_OG_IMAGE_WIDTH,
	documentTitle,
	indexingUrlsFor,
	LocalePathMap,
	ogImageFor,
	SITE_NAME,
	SITE_URL,
	symmetricPaths,
} from "./siteMeta";
import {useLocale} from "i18n";
import {useEffect} from "react";
import {Head} from "vite-react-ssg";
import {serializeJsonLd, synchronizeJsonLdHead} from "./jsonLdHead";

/**
 * Per-page SEO metadata.
 *
 * `vite-react-ssg`'s {@link Head} bridge collects these tags through its Helmet provider. Its SSR
 * adapter writes them into the template's real `<head>` before each static file is emitted. The client
 * provider owns route updates; Seo then reconciles its marked JSON-LD script because Helmet keys inline
 * scripts by their translated contents and can otherwise retain both hydration locales.
 */
// Re-exported for backwards compatibility; the canonical definitions live in ./siteMeta (a
// dependency-free module the build-time sitemap generator can import without pulling in React).
export {SITE_NAME, SITE_URL};

export interface SeoProps {
	/** Page title; the site name is appended automatically unless this is the home page. */
	title: string;
	description: string;
	/**
	 * Path of the canonical URL (e.g. "/about") — the page's path in BOTH trees, since it is mirrored
	 * into a locale→path map via {@link symmetricPaths}. Defaults to "/". Pages whose URL is
	 * locale-asymmetric pass {@link paths} instead.
	 */
	path?: string;
	/**
	 * The page's per-locale paths (ui#1003), for a page whose URL differs between the English and
	 * Romanian trees (localized slugs) or that exists in one language only. Must contain the path for
	 * the locale of the tree/host SERVING it — a locale missing from the map emits no canonical (see
	 * `indexingUrlsFor`), which is what keeps the English host's mirror-only Romanian slugs, reachable
	 * only by in-app navigation, from claiming a URL of their own.
	 */
	paths?: LocalePathMap;
	/** Absolute URL of the social share image. Defaults to the app icon. */
	image?: string;
	/** OpenGraph type. "website" for marketing pages, "article" otherwise. */
	type?: "website" | "article";
	/** Set true to keep the page out of the index (e.g. transient landing shells). */
	noindex?: boolean;
	/**
	 * Optional schema.org JSON-LD, emitted through {@link Head} as a
	 * `<script type="application/ld+json">`. Pass a single entity or an array of entities. See
	 * {@link ./structuredData.ts} for the builders.
	 */
	jsonLd?: object | object[];
}

export default function Seo({
	title,
	description,
	path = "/",
	paths,
	image,
	type = "website",
	noindex = false,
	jsonLd,
}: SeoProps) {
	// HOST WINS (#1135): the site follows the locale this DOCUMENT is published in — the pre-rendered
	// tree's, else the serving host's — never the reader's persisted language. Metadata describes the
	// page at its URL, so transferify.ro keeps declaring transferify.ro for a visitor reading in
	// English; deriving it from the reading language made each brand declare itself a duplicate of the
	// other. See `i18n/localePreference.canonicalLocale`. It is the site the page is CANONICAL on, so a
	// German page names transferify.eu/de.
	const {canonicalLocale} = useLocale();
	const site = canonicalSiteUrl(canonicalLocale);
	// The page's locale→path map: explicit per-locale paths when the page's URL is locale-asymmetric
	// (ui#1003/#1004), otherwise the one `path` mirrored into both trees.
	const pagePaths = paths ?? symmetricPaths(path);
	// Which self-identifying URLs this page may emit. A noindex page emits none — see indexingUrlsFor:
	// one prerendered shell serves the brand hosts AND every claimed subdomain, so any canonical it
	// carried would advertise transferify.eu from a customer's own host.
	const {canonical, ogUrl, alternates} = indexingUrlsFor(canonicalLocale, pagePaths, noindex);
	// Keyed on the page's resolved path for THIS URL's locale, never on the `path` prop's default — a
	// page that describes itself with `paths` would otherwise read as the home page and lose its suffix.
	// The title's WORDS are the reader's (a catalog string the caller passes); only whether this URL is
	// its tree's home page is the document's own property, so the suffix follows the same locale the
	// canonical does.
	const fullTitle = documentTitle(title, canonicalLocale, pagePaths);
	// The default OG cover lives at the root of the host serving that site; only it carries known dimensions.
	const usingDefaultImage = !image;
	const resolvedImage = image ?? ogImageFor(site);
	const serializedJsonLd = serializeJsonLd(jsonLd);

	useEffect(() => {
		synchronizeJsonLdHead(document, serializedJsonLd);
	}, [serializedJsonLd]);

	return (
		<Head defer={false}>
			<title>{fullTitle}</title>
			<meta name="description" content={description}/>
			{canonical && <link rel="canonical" href={canonical}/>}
			{noindex && <meta name="robots" content="noindex, nofollow"/>}
			{/*
			 * Reciprocal hreflang alternates (ui#103, per-page since ui#1003). Each alternate carries
			 * the page's path in ITS OWN locale, so a localized-slug page points at its counterpart's
			 * slug — never at its own path on the other origin, which would be a URL that 404s. Pages
			 * shipped in both trees emit the identical set on both (en ⇄ ro pointing at each other, plus
			 * an x-default → English); the canonical above stays self-referential. A page with no
			 * counterpart in the other language — and every `noindex` shell (the download /
			 * reminder-upload landing pages) — gets NO alternates: they must not advertise a
			 * cross-language equivalent they don't have. `indexingUrlsFor` returns the empty set for all
			 * of those, so the emptiness decision lives beside the canonical/og:url one instead of in a
			 * separate `!noindex &&` guard that could drift from it.
			 */}
			{alternates.map(({hreflang, origin: altOrigin, path: altPath}) => (
				<link
					key={hreflang}
					rel="alternate"
					hrefLang={hreflang}
					href={absoluteUrl(altOrigin, altPath)}
				/>
			))}
			{serializedJsonLd && (
				// Helmet collects inline script content from a string child; dangerouslySetInnerHTML is not
				// part of its child-to-script mapping. The serializer also neutralises closing-tag input.
				<script type="application/ld+json" data-seo-json-ld="true">
					{serializedJsonLd}
				</script>
			)}

			{/* OpenGraph */}
			<meta property="og:site_name" content={SITE_NAME}/>
			<meta property="og:type" content={type}/>
			<meta property="og:title" content={fullTitle}/>
			<meta property="og:description" content={description}/>
			{ogUrl && <meta property="og:url" content={ogUrl}/>}
			<meta property="og:image" content={resolvedImage}/>
			{/* Keep these as direct Head children: Helmet discards metadata nested in a Fragment. */}
			{usingDefaultImage && <meta property="og:image:type" content="image/png"/>}
			{usingDefaultImage && (
				<meta property="og:image:width" content={String(DEFAULT_OG_IMAGE_WIDTH)}/>
			)}
			{usingDefaultImage && (
				<meta property="og:image:height" content={String(DEFAULT_OG_IMAGE_HEIGHT)}/>
			)}

			{/* Twitter */}
			<meta name="twitter:card" content="summary_large_image"/>
			<meta name="twitter:title" content={fullTitle}/>
			<meta name="twitter:description" content={description}/>
			<meta name="twitter:image" content={resolvedImage}/>
		</Head>
	);
}
